import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FallOutlined,
  InfoCircleOutlined,
  PieChartOutlined,
  PlusOutlined,
  RiseOutlined,
  SyncOutlined,
  UnorderedListOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { formatCurrency } from '../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../utils/numberFormatting';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import {
  COMPANY_COST_CATEGORIES,
  COMPANY_COST_CURRENCIES,
  CompanyCostCategory,
  CompanyCostCurrency,
  CompanyCostEntry,
  CompanyCostSource,
  CompanyCostTemplate,
  CompanyCostsSummary,
  createCompanyCostEntry,
  createCompanyCostTemplate,
  deleteCompanyCostEntry,
  deleteCompanyCostTemplate,
  fetchCompanyCostTemplates,
  fetchCompanyCostsMonth,
  MAX_COMPANY_COST_AMOUNT,
  updateCompanyCostEntry,
  updateCompanyCostTemplate,
} from '../modules/companyCosts/api/companyCostsApi';
import { categoryClass } from '../modules/companyCosts/companyCostCategoryColors';
import { POLISH_MONEY_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import {
  fetchCompanyCostGroups,
  groupOptions,
  type CompanyCostGroup,
} from '../modules/companyCosts/api/companyCostGroupsApi';
import CostGroupsView from '../modules/companyCosts/CostGroupsView';
import { ReportFigure, ReportLongTable, ReportSection } from '../modules/reports/ReportTab';
import { ObjectCardEmpty } from '../components/layout/ObjectCard';
import './FixedCosts.css';

const { MonthPicker } = DatePicker;

const MONTH_FORMAT = 'YYYY-MM';

const SOURCE_TAG_COLOR: Record<CompanyCostSource, string> = {
  RECURRING: 'blue',
  ONE_TIME: 'default',
  KSEF: 'purple',
};

interface EntryFormValues {
  name: string;
  category: CompanyCostCategory;
  amount: number;
  currency: CompanyCostCurrency;
  invoiceNumber?: string;
  isPaid?: boolean;
  paymentDate?: Dayjs | null;
  notes?: string;
  groupId?: string | null;
}

interface TemplateFormValues {
  name: string;
  category: CompanyCostCategory;
  amount: number;
  currency: CompanyCostCurrency;
  activeFrom: Dayjs;
  activeTo?: Dayjs | null;
  notes?: string;
  groupId?: string | null;
}

type FixedCostsView = 'month' | 'templates' | 'groups';

// „październik 2026” (lower case inside a sentence or a select); one way of
// writing a month in the whole section (PR2 review).
function monthName(value: string): string {
  return new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${value}-01T00:00:00Z`));
}

// „września 2026” — the genitive of the day-month format („Zmiana do września 2026”).
function monthGenitive(value: string): string {
  const parts = new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'long', timeZone: 'UTC' })
    .formatToParts(new Date(`${value}-01T00:00:00Z`));
  return `${parts.find((part) => part.type === 'month')?.value ?? value} ${value.slice(0, 4)}`;
}

// Whole złoty for the one-line period total of the controls row (the exact
// amount is in its title).
const wholeMoney = (value: number) => formatCurrency(Math.round(value), 'PLN').replace(/,00(?=\s)/, '');

const emptySummary: CompanyCostsSummary = { total: 0, byCategory: {}, entryCount: 0, operatingTotal: 0, belowEbitdaTotal: 0 };

interface FixedCostsProps {
  /**
   * The months of the period of „Wynik firmy” (`YYYY-MM`, PR2 2026-10-29):
   * the month to edit is chosen inside them and the period total is shown.
   * Without it (standalone) the page keeps its own month picker.
   */
  periodMonths?: string[];
  /** Name of the period for the total („2026”, „IV kw. 2026”). */
  periodLabel?: string;
}

/** The month to edit in a period: the current month when it is in it, else the last one. */
export function editableMonthOf(periodMonths: string[], now: Dayjs = dayjs()): string {
  const current = now.format(MONTH_FORMAT);
  return periodMonths.includes(current) ? current : periodMonths[periodMonths.length - 1];
}

const FixedCosts: React.FC<FixedCostsProps> = ({ periodMonths, periodLabel }) => {
  const { t, i18n } = useTranslation(['fixedCosts', 'common']);
  const feedback = useFeedback();

  const [view, setView] = useState<FixedCostsView>('month');

  // ── "Koszty miesiąca" view state ─────────────────────────────────
  const periodKey = periodMonths?.join(',') ?? '';
  const [month, setMonth] = useState<Dayjs>(() => (periodMonths?.length ? dayjs(editableMonthOf(periodMonths), MONTH_FORMAT) : dayjs()));
  // The period of the page moves the month to edit into it.
  useEffect(() => {
    if (periodMonths?.length && !periodMonths.includes(month.format(MONTH_FORMAT))) {
      setMonth(dayjs(editableMonthOf(periodMonths), MONTH_FORMAT));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey]);
  // Fixed costs of the whole period (sum of its months), for the counter.
  // Operating fixed costs of the period (= the group table and the income
  // statement) and the part below EBITDA, named next to it (PR3 review).
  const [periodTotal, setPeriodTotal] = useState<{ operating: number; belowEbitda: number } | null>(null);
  const [periodVersion, setPeriodVersion] = useState(0);
  useEffect(() => {
    if (!periodMonths?.length) return undefined;
    let cancelled = false;
    setPeriodTotal(null);
    Promise.all(periodMonths.map((item) => fetchCompanyCostsMonth(item)))
      .then((responses) => {
        if (cancelled) return;
        setPeriodTotal(responses.reduce((sum, response) => ({
          operating: sum.operating + response.summary.operatingTotal,
          belowEbitda: sum.belowEbitda + response.summary.belowEbitdaTotal,
        }), { operating: 0, belowEbitda: 0 }));
      })
      .catch(() => { if (!cancelled) setPeriodTotal(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey, periodVersion]);
  const [entries, setEntries] = useState<CompanyCostEntry[]>([]);
  const [summary, setSummary] = useState<CompanyCostsSummary>(emptySummary);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Previous-month summary, fetched solely to power the "vs poprzedni
  // miesiąc" delta satellite card. Idempotent GET, same endpoint as the
  // current month — kept in its own state so a slow/failed previous-month
  // fetch never blocks or taints the current month's data.
  const [prevSummary, setPrevSummary] = useState<CompanyCostsSummary | null>(null);
  const [prevLoading, setPrevLoading] = useState(false);

  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CompanyCostEntry | null>(null);
  const [savingEntry, setSavingEntry] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [entryForm] = Form.useForm<EntryFormValues>();
  const entryIsPaid = Form.useWatch('isPaid', entryForm);

  // ── "Koszty cykliczne" tab state ─────────────────────────────────
  const [templates, setTemplates] = useState<CompanyCostTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CompanyCostTemplate | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [togglingTemplateId, setTogglingTemplateId] = useState<string | null>(null);
  const [templateForm] = Form.useForm<TemplateFormValues>();

  // ── Grupy kosztów (MPK) ──────────────────────────────────────────
  const [groups, setGroups] = useState<CompanyCostGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const groupName = useMemo(() => new Map(groups.map((group) => [group.id, group])), [groups]);

  const monthParam = useMemo(() => month.format(MONTH_FORMAT), [month]);
  const prevMonthParam = useMemo(() => month.subtract(1, 'month').format(MONTH_FORMAT), [month]);


  const loadMonth = async (targetMonth: string) => {
    setEntriesLoading(true);
    try {
      const response = await fetchCompanyCostsMonth(targetMonth);
      setEntries(response.entries);
      setSummary(response.summary);
      setPeriodVersion((version) => version + 1);
    } catch {
      feedback.error(t('fixedCosts:month.loadFailed'));
    } finally {
      setEntriesLoading(false);
    }
  };

  const loadPrevMonth = async (targetMonth: string) => {
    setPrevLoading(true);
    try {
      const response = await fetchCompanyCostsMonth(targetMonth);
      setPrevSummary(response.summary);
    } catch {
      // Silent — the delta card degrades to "no data" rather than
      // interrupting the page with an error toast for a secondary figure.
      setPrevSummary(null);
    } finally {
      setPrevLoading(false);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      setTemplates(await fetchCompanyCostTemplates());
    } catch {
      feedback.error(t('fixedCosts:templates.loadFailed'));
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    loadMonth(monthParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthParam]);

  useEffect(() => {
    loadPrevMonth(prevMonthParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevMonthParam]);

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      setGroups(await fetchCompanyCostGroups());
    } catch {
      feedback.error(t('fixedCosts:groups.loadFailed'));
    } finally {
      setGroupsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Entry (one-time cost) modal ──────────────────────────────────
  const openCreateEntry = () => {
    setEditingEntry(null);
    setEntryModalOpen(true);
  };

  const openEditEntry = (entry: CompanyCostEntry) => {
    setEditingEntry(entry);
    setEntryModalOpen(true);
  };

  // Field seeding must happen AFTER the modal is open: antd Modal mounts its
  // children lazily, so setFieldsValue called before the first open targets a
  // not-yet-connected form and the values are silently dropped (the Waluta
  // select rendered empty instead of PLN).
  useEffect(() => {
    if (!entryModalOpen) return;
    // Always reset first (clears the previous open's values), then seed for
    // edit. The form deliberately keeps antd's default preserve=true:
    // paymentDate lives under a conditional Form.Item (visible only when
    // isPaid), and preserve=false drops the seeded value while the field is
    // still unmounted.
    entryForm.resetFields();
    if (editingEntry) {
      entryForm.setFieldsValue({
        name: editingEntry.name,
        category: editingEntry.category,
        amount: editingEntry.originalAmount ?? editingEntry.amount,
        currency: (editingEntry.originalCurrency as CompanyCostCurrency) ?? 'PLN',
        invoiceNumber: editingEntry.invoiceNumber ?? undefined,
        isPaid: editingEntry.isPaid,
        paymentDate: editingEntry.paymentDate ? dayjs(editingEntry.paymentDate) : null,
        notes: editingEntry.notes ?? undefined,
        groupId: editingEntry.groupId ?? null,
      });
    }
  }, [entryModalOpen, editingEntry, entryForm]);

  const closeEntryModal = () => {
    if (savingEntry) return;
    setEntryModalOpen(false);
    setEditingEntry(null);
  };

  const handleEntrySubmit = async (values: EntryFormValues) => {
    setSavingEntry(true);
    try {
      const payload = {
        name: values.name,
        category: values.category,
        amount: Number(values.amount),
        currency: values.currency,
        invoiceNumber: values.invoiceNumber || null,
        isPaid: Boolean(values.isPaid),
        paymentDate: values.isPaid && values.paymentDate ? values.paymentDate.format('YYYY-MM-DD') : null,
        notes: values.notes || null,
        groupId: values.groupId || null,
      };
      if (editingEntry) {
        await updateCompanyCostEntry(editingEntry.id, payload);
        feedback.success(t('fixedCosts:messages.updateSuccess', { name: values.name }));
      } else {
        await createCompanyCostEntry({ ...payload, month: monthParam });
        feedback.success(t('fixedCosts:messages.createSuccess', {
          name: values.name,
          month: monthName(monthParam),
        }));
      }
      setEntryModalOpen(false);
      setEditingEntry(null);
      await loadMonth(monthParam);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.saveFailed', undefined, { name: values.name }),
      );
    } finally {
      setSavingEntry(false);
    }
  };

  const handleDeleteEntry = async (entry: CompanyCostEntry) => {
    setDeletingEntryId(entry.id);
    try {
      await deleteCompanyCostEntry(entry.id);
      feedback.success(t('fixedCosts:messages.deleteSuccess', {
        name: entry.name,
        month: monthName(entry.month),
      }));
      await loadMonth(monthParam);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.deleteFailed', undefined, { name: entry.name }),
      );
    } finally {
      setDeletingEntryId(null);
    }
  };

  // ── Template (recurring cost) modal ──────────────────────────────
  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (template: CompanyCostTemplate) => {
    setEditingTemplate(template);
    setTemplateModalOpen(true);
  };

  // Same lazy-mount seeding rule as the entry modal above.
  useEffect(() => {
    if (!templateModalOpen) return;
    templateForm.resetFields();
    if (editingTemplate) {
      templateForm.setFieldsValue({
        name: editingTemplate.name,
        category: editingTemplate.category,
        amount: editingTemplate.originalAmount ?? editingTemplate.amount,
        currency: (editingTemplate.originalCurrency as CompanyCostCurrency) ?? 'PLN',
        activeFrom: dayjs(editingTemplate.activeFrom, MONTH_FORMAT),
        activeTo: editingTemplate.activeTo ? dayjs(editingTemplate.activeTo, MONTH_FORMAT) : null,
        notes: editingTemplate.notes ?? undefined,
        groupId: editingTemplate.groupId ?? null,
      });
    } else {
      templateForm.setFieldsValue({ activeFrom: month });
    }
  }, [templateModalOpen, editingTemplate, templateForm, month]);

  const closeTemplateModal = () => {
    if (savingTemplate) return;
    setTemplateModalOpen(false);
    setEditingTemplate(null);
  };

  const handleTemplateSubmit = async (values: TemplateFormValues) => {
    setSavingTemplate(true);
    try {
      const payload = {
        name: values.name,
        category: values.category,
        amount: Number(values.amount),
        currency: values.currency,
        activeFrom: values.activeFrom.format(MONTH_FORMAT),
        activeTo: values.activeTo ? values.activeTo.format(MONTH_FORMAT) : null,
        notes: values.notes || null,
        groupId: values.groupId || null,
      };
      if (editingTemplate) {
        const result = await updateCompanyCostTemplate(editingTemplate.id, payload);
        feedback.success(t('fixedCosts:messages.templateUpdateSuccess', { name: values.name }));
        if ((result.regroupedMonths ?? 0) > 0) {
          feedback.info(t('fixedCosts:messages.templateRegrouped', { count: result.regroupedMonths }));
        }
        if (result.prunedMonths > 0) {
          feedback.info(t('fixedCosts:messages.templateWindowPruned', { count: result.prunedMonths }));
        }
      } else {
        await createCompanyCostTemplate(payload);
        feedback.success(t('fixedCosts:messages.templateCreateSuccess', { name: values.name }));
      }
      setTemplateModalOpen(false);
      setEditingTemplate(null);
      await Promise.all([loadTemplates(), loadMonth(monthParam)]);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.templateSaveFailed', undefined, { name: values.name }),
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleTemplateActive = async (template: CompanyCostTemplate) => {
    setTogglingTemplateId(template.id);
    const activating = !template.isActive;
    try {
      await updateCompanyCostTemplate(template.id, { isActive: activating });
      feedback.success(
        t(
          activating
            ? 'fixedCosts:messages.templateActivateSuccess'
            : 'fixedCosts:messages.templateDeactivateSuccess',
          { name: template.name },
        ),
      );
      await Promise.all([loadTemplates(), loadMonth(monthParam)]);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.templateStatusUpdateFailed', undefined, {
          name: template.name,
        }),
      );
    } finally {
      setTogglingTemplateId(null);
    }
  };

  const handleDeleteTemplate = async (template: CompanyCostTemplate) => {
    setDeletingTemplateId(template.id);
    try {
      await deleteCompanyCostTemplate(template.id);
      feedback.success(t('fixedCosts:messages.templateDeleteSuccess', { name: template.name }));
      await loadTemplates();
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.templateDeleteFailed', {
          COMPANY_COST_TEMPLATE_INVOICE_LINKED: 'fixedCosts:messages.templateDeleteInvoiceLinked',
        }, {
          name: template.name,
        }),
      );
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // ── "Koszty miesiąca" derived figures ────────────────────────────
  const categoryBreakdown = useMemo(() => (
    (Object.entries(summary.byCategory) as [CompanyCostCategory, number][])
      .filter(([, amount]) => Number(amount) !== 0)
      .map(([category, amount]) => ({ category, amount: Number(amount) }))
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
  ), [summary.byCategory]);

  const categoryTotal = useMemo(
    () => categoryBreakdown.reduce((sum, row) => sum + Math.abs(row.amount), 0) || 1,
    [categoryBreakdown],
  );

  const paidAmount = useMemo(
    () => entries.filter((entry) => entry.isPaid).reduce((sum, entry) => sum + entry.amount, 0),
    [entries],
  );
  const unpaidAmount = useMemo(
    () => entries.filter((entry) => !entry.isPaid).reduce((sum, entry) => sum + entry.amount, 0),
    [entries],
  );

  const paidCount = useMemo(() => entries.filter((entry) => entry.isPaid).length, [entries]);

  // ── Month-over-month delta (fourth figure tile) ──────────────────
  // Missing previous month is "—", never 0 zł; growth of costs is the worse
  // direction, so it takes the danger colour.
  const deltaFigure = useMemo((): { value: string; note?: string; cls?: string; grew?: boolean } => {
    const prevMonthLabel = monthName(month.subtract(1, 'month').format(MONTH_FORMAT));
    if (prevLoading) return { value: '…', cls: 'is-missing' };
    if (!prevSummary || prevSummary.entryCount === 0) {
      return { value: '—', cls: 'is-missing', note: t('fixedCosts:satellites.deltaNoDataMonth', { month: prevMonthLabel }) };
    }
    const diff = summary.operatingTotal - prevSummary.operatingTotal;
    if (diff === 0) return { value: formatCurrency(0, 'PLN'), note: t('fixedCosts:satellites.deltaFlat') };
    const grew = diff > 0;
    const pct = prevSummary.operatingTotal > 0 ? (Math.abs(diff) / prevSummary.operatingTotal) * 100 : null;
    return {
      value: `${grew ? '+' : '−'}${formatCurrency(Math.abs(diff), 'PLN')}`,
      cls: grew ? 'is-negative' : 'is-positive',
      grew,
      note: t(grew ? 'fixedCosts:satellites.deltaUp' : 'fixedCosts:satellites.deltaDown', {
        percent: pct !== null ? formatLocalizedPercent(pct, i18n.language) : '—',
      }),
    };
  }, [prevLoading, prevSummary, summary.operatingTotal, i18n.language, t, month]);

  // ── "Koszty cykliczne" derived figures ───────────────────────────
  const activeTemplates = useMemo(() => templates.filter((tpl) => tpl.isActive), [templates]);
  const activeTemplatesMonthlySum = useMemo(
    () => activeTemplates.reduce((sum, tpl) => sum + tpl.amount, 0),
    [activeTemplates],
  );

  function entryRowClassName(record: CompanyCostEntry): string {
    return record.isPaid ? 'fixedcosts-row' : 'fixedcosts-row fixedcosts-row--unpaid';
  }

  function templateRowClassName(record: CompanyCostTemplate): string {
    return record.isActive ? 'fixedcosts-row' : 'fixedcosts-row fixedcosts-row--inactive';
  }

  // „Bez grupy” muted; an archived group keeps its name with a tag.
  const renderGroup = (groupId?: string | null) => {
    const group = groupId ? groupName.get(groupId) : undefined;
    if (!group) return <span className="report-muted">{t('fixedCosts:groups.none')}</span>;
    const label = group.isActive ? group.name : `${group.name} · ${t('fixedCosts:groups.archivedTag')}`;
    return <span className="report-cut fixedcosts-name" title={label}>{label}</span>;
  };
  const groupColumn = {
    title: t('fixedCosts:table.group'),
    dataIndex: 'groupId',
    key: 'groupId',
    render: (groupId?: string | null) => renderGroup(groupId),
  };
  const groupSelect = (currentId?: string | null) => (
    <Select
      allowClear
      placeholder={t('fixedCosts:groups.none')}
      options={groupOptions(groups, currentId).map((group) => ({
        value: group.id,
        label: group.isActive ? group.name : `${group.name} · ${t('fixedCosts:groups.archivedTag')}`,
      }))}
      notFoundContent={t('fixedCosts:groups.noneYet')}
      data-testid="fixedcosts-group-select"
    />
  );

  const entryColumns: TableColumnsType<CompanyCostEntry> = [
    {
      title: t('fixedCosts:table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span className="report-cut fixedcosts-name" title={name}>{name}</span>,
    },
    {
      title: t('fixedCosts:table.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category: CompanyCostCategory) => (
        <span className="fixedcosts-table-category">
          <span className={`fixedcosts-category-dot ${categoryClass(category)}`} />
          {t(`fixedCosts:categories.${category}`, { defaultValue: category })}
        </span>
      ),
    },
    groupColumn,
    {
      title: t('fixedCosts:table.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, record) => (
        <div className="fixedcosts-table-amount">
          <div className="fixedcosts-table-amount__main">{formatCurrency(record.amount, 'PLN')}</div>
          {record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null && (
            <div className="fixedcosts-table-amount__original">
              {formatCurrency(record.originalAmount, record.originalCurrency)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('fixedCosts:table.source'),
      dataIndex: 'source',
      key: 'source',
      render: (source: CompanyCostSource) => (
        <Tag color={SOURCE_TAG_COLOR[source] ?? 'default'}>{t(`fixedCosts:source.${source}`, { defaultValue: source })}</Tag>
      ),
    },
    {
      title: t('fixedCosts:table.invoiceNumber'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (value?: string | null) => value || '—',
    },
    {
      title: t('fixedCosts:table.paid'),
      key: 'isPaid',
      render: (_value, record) => (
        <Tag color={record.isPaid ? 'success' : 'default'}>
          {record.isPaid ? t('fixedCosts:table.paid') : t('fixedCosts:table.unpaid')}
        </Tag>
      ),
    },
    {
      title: t('fixedCosts:table.actions'),
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditEntry(record)} />
          </Tooltip>
          <Popconfirm
            title={t('fixedCosts:messages.deleteConfirmEntry', {
              name: record.name,
              month: monthName(record.month),
            })}
            okText={t('common:yes')}
            cancelText={t('common:no')}
            onConfirm={() => {
              void handleDeleteEntry(record);
            }}
          >
            <Tooltip title={t('common:delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} loading={deletingEntryId === record.id} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const viewSwitch = (
    <Segmented
      className="report-tab__views"
      value={view}
      onChange={(value) => setView(value as FixedCostsView)}
      options={[
        { value: 'month', label: t('fixedCosts:tabs.month') },
        { value: 'templates', label: t('fixedCosts:tabs.templates') },
        { value: 'groups', label: t('fixedCosts:tabs.groups') },
      ]}
    />
  );

  const figures = (
    <div className="report-figures">
      <ReportFigure
        icon={<WalletOutlined />}
        tone="blue"
        label={t('fixedCosts:hero.kicker')}
        value={formatCurrency(summary.operatingTotal, 'PLN')}
        note={summary.belowEbitdaTotal !== 0
          ? t('fixedCosts:figures.belowEbitdaNote', { amount: formatCurrency(summary.belowEbitdaTotal, 'PLN') })
          : t('fixedCosts:figures.totalNote', { count: summary.entryCount })}
        noteTitle={summary.belowEbitdaTotal !== 0
          ? t('fixedCosts:figures.belowEbitdaHint', {
            amount: formatCurrency(summary.belowEbitdaTotal, 'PLN'),
            total: formatCurrency(summary.total, 'PLN'),
          })
          : undefined}
      />
      <ReportFigure
        icon={<CheckOutlined />}
        tone="green"
        label={t('fixedCosts:satellites.paidTitle')}
        value={formatCurrency(paidAmount, 'PLN')}
        note={t('fixedCosts:figures.entriesNote', { count: paidCount })}
      />
      <ReportFigure
        icon={<ClockCircleOutlined />}
        tone="orange"
        label={t('fixedCosts:satellites.dueTitle')}
        value={formatCurrency(unpaidAmount, 'PLN')}
        note={t('fixedCosts:figures.entriesNote', { count: entries.length - paidCount })}
      />
      <ReportFigure
        icon={deltaFigure.grew === false ? <FallOutlined /> : <RiseOutlined />}
        tone="indigo"
        label={t('fixedCosts:satellites.deltaTitle', { month: monthGenitive(month.subtract(1, 'month').format(MONTH_FORMAT)) })}
        value={deltaFigure.value}
        valueClassName={deltaFigure.cls}
        note={deltaFigure.note}
      />
    </div>
  );

  const categorySection = categoryBreakdown.length > 0 && (
    <ReportSection
      icon={<PieChartOutlined />}
      tone="indigo"
      title={t('fixedCosts:categoryBreakdown.title')}
      lead={t('fixedCosts:categoryBreakdown.lead')}
    >
      <div className="fixedcosts-categories">
        {categoryBreakdown.map(({ category, amount }) => {
          const share = (Math.abs(amount) / categoryTotal) * 100;
          return (
            <div className="fixedcosts-category-row" key={category}>
              <span className="fixedcosts-category-row__name">
                <span className={`fixedcosts-category-dot ${categoryClass(category)}`} />
                {t(`fixedCosts:categories.${category}`, { defaultValue: category })}
              </span>
              <span className="fixedcosts-category-row__bar">
                <span
                  className={`fixedcosts-category-row__fill ${categoryClass(category)}`}
                  style={{ width: `${Math.min(100, Math.max(2, share))}%` }}
                />
              </span>
              <span className="fixedcosts-category-row__amount">{formatCurrency(amount, 'PLN')}</span>
              <span className="fixedcosts-category-row__share">{formatLocalizedNumber(share, 0, i18n.language)}%</span>
            </div>
          );
        })}
      </div>
    </ReportSection>
  );

  const monthTab = (
    <div className="report-tab fixedcosts-page">
      <div className="report-controls">
        {viewSwitch}
        {periodMonths?.length ? (
          // Inside „Wynik firmy” the month to edit is one of the period's (PR2).
          periodMonths.length > 1 ? (
            <Select
              value={month.format(MONTH_FORMAT)}
              onChange={(value: string) => setMonth(dayjs(value, MONTH_FORMAT))}
              options={periodMonths.map((item) => ({ value: item, label: monthName(item) }))}
              // Short prefix: the controls row has three views since „Grupy kosztów” (one line at 1440 px).
              prefix={t('fixedCosts:month.editPrefix')}
              aria-label={t('fixedCosts:month.editMonth')}
              className="fixedcosts-month-select"
              popupMatchSelectWidth={false}
            />
          ) : (
            <span className="report-controls__counter">{t('fixedCosts:month.editMonthLabel', { month: monthName(periodMonths[0]) })}</span>
          )
        ) : (
          <MonthPicker
            value={month}
            onChange={(date) => date && setMonth(date)}
            format="MM.YYYY"
            allowClear={false}
            aria-label={t('fixedCosts:month.selectMonth')}
          />
        )}
        <div className="report-controls__end">
          <span
            className="report-controls__counter"
            data-testid="fixedcosts-period-total"
            title={periodTotal && periodTotal.belowEbitda !== 0 && periodMonths && periodMonths.length > 1
              ? t('fixedCosts:month.periodTotalSplitHint', {
                period: periodLabel ?? '',
                amount: formatCurrency(periodTotal.operating, 'PLN'),
                below: formatCurrency(periodTotal.belowEbitda, 'PLN'),
              })
              : undefined}
          >
            {periodMonths?.length && periodMonths.length > 1
              ? (periodTotal && periodTotal.belowEbitda !== 0
                ? t('fixedCosts:month.periodTotalSplit', {
                  period: periodLabel ?? '',
                  amount: wholeMoney(periodTotal.operating),
                  below: wholeMoney(periodTotal.belowEbitda),
                })
                : t('fixedCosts:month.periodTotal', { period: periodLabel ?? '', amount: periodTotal == null ? '…' : wholeMoney(periodTotal.operating) }))
              : t('fixedCosts:month.counter', { count: summary.entryCount, month: monthName(month.format(MONTH_FORMAT)) })}
          </span>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEntry}>
            {t('fixedCosts:month.addCost')}
          </Button>
        </div>
      </div>

      {figures}
      {categorySection}

      <ReportSection
        icon={<UnorderedListOutlined />}
        tone="blue"
        title={t('fixedCosts:entries.title')}
        count={entries.length}
        lead={t('fixedCosts:entries.lead')}
      >
        <ReportLongTable rowCount={entries.length} testId="fixed-costs-entries-rows">
          <Table<CompanyCostEntry>
            className="report-table"
            columns={entryColumns}
            dataSource={entries}
            rowKey="id"
            rowClassName={entryRowClassName}
            loading={entriesLoading}
            size="middle"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{
              emptyText: (
                <ObjectCardEmpty
                  icon={<WalletOutlined />}
                  text={t('fixedCosts:month.empty', { month: monthName(month.format(MONTH_FORMAT)) })}
                  action={(
                    <Button icon={<PlusOutlined />} onClick={openCreateEntry}>
                      {t('fixedCosts:month.addCost')}
                    </Button>
                  )}
                />
              ),
            }}
          />
        </ReportLongTable>
      </ReportSection>

      <Modal
        title={editingEntry ? t('fixedCosts:entryModal.editTitle') : t('fixedCosts:entryModal.addTitle')}
        open={entryModalOpen}
        onCancel={closeEntryModal}
        onOk={() => entryForm.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingEntry}
        maskClosable={false}
        destroyOnClose
        // Filled fields like the object cards; fits a 790 px high screen with its buttons.
        className="obj-drawer"
        style={{ top: 24 }}
      >
        <Form
          form={entryForm}
          layout="vertical"
          onFinish={handleEntrySubmit}
          initialValues={{ currency: 'PLN', isPaid: false }}
        >
          <Form.Item
            name="name"
            label={t('fixedCosts:entryModal.name')}
            rules={[{ required: true, message: t('fixedCosts:entryModal.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="category"
            label={t('fixedCosts:entryModal.category')}
            rules={[{ required: true, message: t('fixedCosts:entryModal.categoryRequired') }]}
          >
            <Select
              options={COMPANY_COST_CATEGORIES.map((category) => ({
                value: category,
                label: t(`fixedCosts:categories.${category}`),
              }))}
            />
          </Form.Item>

          <Form.Item name="groupId" label={t('fixedCosts:entryModal.group')} tooltip={t('fixedCosts:groups.fieldHint')}>
            {groupSelect(editingEntry?.groupId)}
          </Form.Item>

          <div className="fixedcosts-form-row">
            <Form.Item
              name="amount"
              label={t('fixedCosts:entryModal.amount')}
              rules={[
                { required: true, message: t('fixedCosts:entryModal.amountRequired') },
                {
                  type: 'number',
                  min: 0.01,
                  max: MAX_COMPANY_COST_AMOUNT,
                  message: t('fixedCosts:entryModal.amountRange', { max: MAX_COMPANY_COST_AMOUNT }),
                },
              ]}
            >
              <InputNumber className="fixedcosts-form-row__control" step={0.01} precision={2} {...POLISH_MONEY_INPUT_NUMBER_PROPS} />
            </Form.Item>
            <Form.Item name="currency" label={t('fixedCosts:entryModal.currency')}>
              <Select options={COMPANY_COST_CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
            </Form.Item>
          </div>

          <Form.Item name="invoiceNumber" label={t('fixedCosts:entryModal.invoiceNumber')}>
            <Input />
          </Form.Item>

          <Form.Item name="isPaid" label={t('fixedCosts:entryModal.isPaid')} valuePropName="checked">
            <Switch />
          </Form.Item>

          {entryIsPaid && (
            <Form.Item name="paymentDate" label={t('fixedCosts:entryModal.paymentDate')}>
              <DatePicker className="fixedcosts-form-row__control" />
            </Form.Item>
          )}

          <Form.Item name="notes" label={t('fixedCosts:entryModal.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  // ── "Koszty cykliczne" table ──────────────────────────────────────
  const templateColumns: TableColumnsType<CompanyCostTemplate> = [
    {
      title: t('fixedCosts:table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span className="report-cut fixedcosts-name" title={name}>{name}</span>,
    },
    {
      title: t('fixedCosts:table.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category: CompanyCostCategory) => (
        <span className="fixedcosts-table-category">
          <span className={`fixedcosts-category-dot ${categoryClass(category)}`} />
          {t(`fixedCosts:categories.${category}`, { defaultValue: category })}
        </span>
      ),
    },
    groupColumn,
    {
      title: t('fixedCosts:table.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, record) => (
        <div className="fixedcosts-table-amount">
          <div className="fixedcosts-table-amount__main">{formatCurrency(record.amount, 'PLN')}</div>
          {record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null && (
            <div className="fixedcosts-table-amount__original">
              {formatCurrency(record.originalAmount, record.originalCurrency)}
            </div>
          )}
        </div>
      ),
    },
    {
      // Short header since the „Grupa” column (13″ MacBook width); the months say „od — do”.
      title: t('fixedCosts:templates.activeRange'),
      key: 'activeRange',
      render: (_value, record) => {
        let progress: number | null = null;
        if (record.activeTo) {
          const from = dayjs(record.activeFrom, MONTH_FORMAT);
          const to = dayjs(record.activeTo, MONTH_FORMAT);
          const totalMonths = Math.max(1, to.diff(from, 'month'));
          const elapsed = Math.min(totalMonths, Math.max(0, dayjs().diff(from, 'month')));
          progress = Math.min(100, Math.max(0, (elapsed / totalMonths) * 100));
        }
        return (
          <div className="fixedcosts-range">
            <div className="fixedcosts-range__text">{record.activeFrom} — {record.activeTo || '—'}</div>
            {progress !== null && (
              <div className="fixedcosts-range__bar">
                <div className="fixedcosts-range__bar-fill" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('fixedCosts:templates.status'),
      key: 'status',
      render: (_value, record) => (
        <Tag color={record.isActive ? 'success' : 'default'}>
          {record.isActive ? t('fixedCosts:templates.active') : t('fixedCosts:templates.inactive')}
        </Tag>
      ),
    },
    {
      title: t('fixedCosts:table.actions'),
      key: 'actions',
      width: 170,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditTemplate(record)} />
          </Tooltip>
          <Button
            size="small"
            onClick={() => handleToggleTemplateActive(record)}
            loading={togglingTemplateId === record.id}
          >
            {record.isActive ? t('fixedCosts:templates.deactivate') : t('fixedCosts:templates.activate')}
          </Button>
          <Popconfirm
            title={t('fixedCosts:templates.deleteConfirmTitle')}
            description={t('fixedCosts:templates.deleteConfirmDescription')}
            okText={t('common:yes')}
            cancelText={t('common:no')}
            onConfirm={() => {
              void handleDeleteTemplate(record);
            }}
          >
            <Tooltip title={t('common:delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} loading={deletingTemplateId === record.id} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const templatesTab = (
    <div className="report-tab fixedcosts-page">
      <div className="report-controls">
        {viewSwitch}
        <div className="report-controls__end">
          <span className="report-controls__counter">
            {t('fixedCosts:templatesSummary.counter', { active: activeTemplates.length, total: templates.length })}
          </span>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateTemplate}>
            {t('fixedCosts:templates.addTemplate')}
          </Button>
        </div>
      </div>

      <div className="report-figures">
        <ReportFigure
          icon={<SyncOutlined />}
          tone="teal"
          label={t('fixedCosts:templatesSummary.activeCount')}
          value={activeTemplates.length}
          note={t('fixedCosts:templatesSummary.activeCountNote', { count: templates.length })}
        />
        <ReportFigure
          icon={<WalletOutlined />}
          tone="blue"
          label={t('fixedCosts:templatesSummary.activeMonthlySum')}
          value={formatCurrency(activeTemplatesMonthlySum, 'PLN')}
          note={t('fixedCosts:templatesSummary.activeMonthlySumNote')}
        />
      </div>

      <ReportSection
        icon={<SyncOutlined />}
        tone="teal"
        title={t('fixedCosts:tabs.templates')}
        count={templates.length}
        lead={t('fixedCosts:templates.lead')}
        actions={(
          <Tooltip title={t('fixedCosts:templates.infoAlert')} placement="topRight">
            <InfoCircleOutlined className="fixedcosts-info" aria-label={t('fixedCosts:templates.infoAlert')} />
          </Tooltip>
        )}
      >
        <ReportLongTable rowCount={templates.length} testId="fixed-costs-templates-rows">
          <Table<CompanyCostTemplate>
            className="report-table"
            columns={templateColumns}
            dataSource={templates}
            rowKey="id"
            rowClassName={templateRowClassName}
            loading={templatesLoading}
            size="middle"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{
              emptyText: (
                <ObjectCardEmpty
                  icon={<SyncOutlined />}
                  text={t('fixedCosts:templates.emptyHint')}
                  action={(
                    <Button icon={<PlusOutlined />} onClick={openCreateTemplate}>
                      {t('fixedCosts:templates.addTemplate')}
                    </Button>
                  )}
                />
              ),
            }}
          />
        </ReportLongTable>
      </ReportSection>

      <Modal
        title={editingTemplate ? t('fixedCosts:templateModal.editTitle') : t('fixedCosts:templateModal.addTitle')}
        open={templateModalOpen}
        onCancel={closeTemplateModal}
        onOk={() => templateForm.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingTemplate}
        maskClosable={false}
        destroyOnClose
        // Filled fields like the object cards; fits a 790 px high screen with its buttons.
        className="obj-drawer"
        style={{ top: 24 }}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={handleTemplateSubmit}
          initialValues={{ currency: 'PLN' }}
        >
          <Form.Item
            name="name"
            label={t('fixedCosts:templateModal.name')}
            rules={[{ required: true, message: t('fixedCosts:templateModal.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="category"
            label={t('fixedCosts:templateModal.category')}
            rules={[{ required: true, message: t('fixedCosts:templateModal.categoryRequired') }]}
          >
            <Select
              options={COMPANY_COST_CATEGORIES.map((category) => ({
                value: category,
                label: t(`fixedCosts:categories.${category}`),
              }))}
            />
          </Form.Item>

          <Form.Item
            name="groupId"
            label={t('fixedCosts:templateModal.group')}
            tooltip={t('fixedCosts:groups.templateFieldHint')}
          >
            {groupSelect(editingTemplate?.groupId)}
          </Form.Item>

          <div className="fixedcosts-form-row">
            <Form.Item
              name="amount"
              label={t('fixedCosts:templateModal.amount')}
              rules={[
                { required: true, message: t('fixedCosts:templateModal.amountRequired') },
                {
                  type: 'number',
                  min: 0.01,
                  max: MAX_COMPANY_COST_AMOUNT,
                  message: t('fixedCosts:templateModal.amountRange', { max: MAX_COMPANY_COST_AMOUNT }),
                },
              ]}
            >
              <InputNumber className="fixedcosts-form-row__control" step={0.01} precision={2} {...POLISH_MONEY_INPUT_NUMBER_PROPS} />
            </Form.Item>
            <Form.Item name="currency" label={t('fixedCosts:templateModal.currency')}>
              <Select options={COMPANY_COST_CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
            </Form.Item>
          </div>

          <div className="fixedcosts-form-row fixedcosts-form-row--even">
            <Form.Item
              name="activeFrom"
              label={t('fixedCosts:templateModal.activeFrom')}
              rules={[{ required: true, message: t('fixedCosts:templateModal.activeFromRequired') }]}
            >
              <MonthPicker className="fixedcosts-form-row__control" format={MONTH_FORMAT} />
            </Form.Item>
            <Form.Item
              name="activeTo"
              label={t('fixedCosts:templateModal.activeTo')}
              tooltip={t('fixedCosts:templateModal.activeToHint')}
            >
              <MonthPicker className="fixedcosts-form-row__control" format={MONTH_FORMAT} allowClear />
            </Form.Item>
          </div>

          <Form.Item name="notes" label={t('fixedCosts:templateModal.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  if (view === 'groups') {
    return (
      <CostGroupsView
        groups={groups}
        loading={groupsLoading}
        viewSwitch={viewSwitch}
        onChanged={async () => { await Promise.all([loadGroups(), loadTemplates()]); }}
      />
    );
  }
  return view === 'month' ? monthTab : templatesTab;
};

export default FixedCosts;
